import React, { useRef, useContext, useState } from "react";
import { Icon, Image, Button,Ref, Segment } from "semantic-ui-react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { ResumeStoreContext } from "../../stores/ResumeStore";
import "./LinkedInView.css";
import { trackAction } from "../../tracking";
import OnboardingActions from "../../tracking/actions";
import DebouncedInput from "./DebouncedInput"
import { openLinkedIn } from "../../lib/linkedin-connector";

const containerStyle = {
  background: "white",
  margin: "20px 0",
  padding: "20px 40px 20px 40px"
};

const detectSafari  = () => {
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  if ((navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1  && !iOS) ) {
    return true;
  }
  return false;
};

const detectedSafari = detectSafari();

export default observer(function LinkedInView() {
  const resumeStore = useContext(ResumeStoreContext);
  const [t] = useTranslation();
  const containerRef = useRef();

  const [profileURL, setProfileURL] = useState(resumeStore.linkedInProfileUrl || '');
  const profileURLInputRef = useRef();
  const [externalWindow, setExternalWindow] = useState(null)
  const [localError, setLocalError] = useState("")
  const safariInstructions = t('li-safariInstructions')

  /*
  * This retrieves the position for the auth and profile windows.
  * It is based on the profile instructions container and the popup windows width to properly position the window so...
  * that it is relative to the instructions that direct it
  * */
  function getPopUpPosition(popupWidth = 400, popupHeight = 400){
    const containerRect = containerRef.current.getBoundingClientRect();
    const padding = 50;
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    const targetX = window.screenX + containerRect.left - scrollX;
    const targetY = Math.max(
      padding,
      Math.min(
        window.screenY + containerRect.top - scrollY,
        window.screen.height - popupHeight - padding
      )
    );

    const targetRight = targetX + containerRect.width + popupWidth;
    return {
      top: targetY,
      left: targetRight < window.screen.width
        ? targetX + containerRect.width 
        : targetX - popupWidth
    }
  }

  function doLIAuth(){
      const {left, top} = getPopUpPosition(400, 600);
      setLocalError("")
      openLinkedIn(true, true, `top=${top}, left=${left},width=400,height=600,location=yes`)
        .then(res=>{
          // get data
          resumeStore.linkedInToken = res.token;
        })
        .catch(err => {
          if (err.message && err.message === "popup blocked") {
            resumeStore.hasError = {
              status: 403
              ,statusText: err.message
            };
            setLocalError('Remove popup blocker to continue.')
          } else {
            resumeStore.hasError = {
              status: err.status,
              statusText: err.statusText
            };
            setLocalError(t("li_auth_error"))
          }
          trackAction(OnboardingActions.LINKEDINURLERROR, err);
        })
  }

  function openProfileWindow(){
    !!externalWindow && externalWindow.close();
    const {left, top} = getPopUpPosition(400, 400);
    setExternalWindow(window.open('http://linkedin.com/in/', '',  `top=${top}, left=${left},width=400,height=400,location=yes`))
  }

  function closeLinkedInAuth(){
    externalWindow.close();
    setExternalWindow(null);
  }


  const saveProfileUrlOnChange = (e) => {
    const value = e;
    setProfileURL(value);
    if (/^https*:\/\/(www\.)*linkedin\.com\/in\/[^/]+/i.test(value)) {
      !!externalWindow && closeLinkedInAuth();
      resumeStore.saveLinkedInURL(value)
        .catch(err => {
          console.log("Commit LinkedIn profile url",JSON.stringify(err))
          setLocalError(t("li_save_url_error"))
        })
      setLocalError("")
    } else {
      if(value !== ""){
        setLocalError(t("enter_valid_url"))
      }
    }
  };

  if (resumeStore.linkedInProfileUrl && !resumeStore.linkedInProfileUrlChangedByUser){
    return <>

        <Segment style={containerStyle} textAlign="center">
          <div className="linkedin-container">
            <img style={{ height: 20, verticalAlign: "middle" }} src={`images/Logo-2C-21px-TM.png`} alt="LinkedIn" />
            <div className="linkedin-check">
              <Icon className="icon-check-4-1" color="green" size="large" />
            </div>
          </div>

          <p style={{ marginTop: "20px" }}>{t("linkedin_sub_heading")}</p>
        </Segment>
      </>;
  }


  return (
    <Ref innerRef={node => (containerRef.current = node)}>
      <Segment
      style={containerStyle}
      textAlign="center"
    >
      {(resumeStore.linkedInToken) ? (
        <>
          <p style={{ marginTop: "20px", textAlign: "left"  }}>
            {t("li_instructions_header")}
          </p>
          <ol  style={{ textAlign: "left" , paddingLeft: "15px"}}>
            <li style={{ lineHeight: "30px"}}>{t("li_step_1")}</li>
            <li style={{ lineHeight: "30px"}}>{t("li_step_2")} {detectedSafari && safariInstructions}
                { detectedSafari && <Image className={'li-profile-safari-instructions'} src={"images/LinkedIn_Screen.png"} /> }
           </li>
            <li style={{ lineHeight: "30px"}}>{t("li_step_3")}</li>
          </ol>

          <div className={"li-profile-url-controls-container"}>
            <Button
              style={{minWidth: "188px"}}
              primary
              onClick={()=>openProfileWindow()}>
              {t("li_view_profile_btn_label")}
            </Button>

            <Ref innerRef={node => profileURLInputRef.current = node}>
              <DebouncedInput
                style={{width:"100%"}}
                type={"url"}
                name={"linkedInProfileUrl"}
                placeholder={t("li_url_input_placeholder")}
                defaultValue={profileURL}
                onChangeDebounced={saveProfileUrlOnChange}
                onChange={() => {
                  resumeStore.showConfirmationStatusBar = false;
                  setLocalError("")
                }}
                />
              </Ref>
            </div>
        </>
      ) : (
        <>
          {t("li_sign_in_heading")}
          <br />
          <br />
          <div className="linkedin-container-sign-in" onClick={doLIAuth}>
            <img
              style={{ height: 20, verticalAlign: "middle" }}
              src={`images/In-2C-21px-TM.png`}
              alt="LinkedIn"
            />
            &nbsp;<strong>{t("li_sign_in_btn_label")}</strong>
          </div>
        </>
      )}
      {!!localError &&
      <div style={{color:"red", marginTop: "1rem"}}>
        {localError}
      </div>
      }
    </Segment>
    </Ref>
  );
})